import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { WQE } from "@/config/weeklyqe";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WeeklyQEAdmin() {
  const [token, setToken] = useState("");
  const [recordingUrl, setRecordingUrl] = useState("");
  const [topicSummary, setTopicSummary] = useState("");
  const [personalNote, setPersonalNote] = useState("");
  const [reflection, setReflection] = useState("");
  const [announcement, setAnnouncement] = useState("");
  const [status, setStatus] = useState(null); // {ok:bool, message, count}
  const [memberCount, setMemberCount] = useState(null);
  const [sending, setSending] = useState(false);

  const loadMemberCount = async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/weeklyqe/admin/members`, {
        headers: { "X-Admin-Token": token },
      });
      setMemberCount(data.active_count);
    } catch (e) {
      setMemberCount(null);
      setStatus({ ok: false, message: e.response?.data?.detail || "Failed to load members. Check your token." });
    }
  };

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);
    try {
      const { data } = await axios.post(
        `${API}/weeklyqe/admin/send-followup`,
        {
          recording_url: recordingUrl.trim(),
          topic_summary: topicSummary.trim(),
          personal_note: personalNote.trim(),
          reflection: reflection.trim(),
          announcement: announcement.trim(),
        },
        { headers: { "X-Admin-Token": token } }
      );
      setStatus({ ok: true, message: `Sent to ${data.recipients_count} members.`, count: data.recipients_count });
      setRecordingUrl(""); setTopicSummary(""); setPersonalNote(""); setReflection(""); setAnnouncement("");
    } catch (e) {
      setStatus({ ok: false, message: e.response?.data?.detail || "Send failed." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="wqe-admin-page">
      <header className="border-b border-border/40">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-4 flex items-center gap-4">
          <img src={WQE.logoUrl} alt={WQE.parentBrand} className="h-7" />
          <span className="text-xs uppercase tracking-[0.24em] text-muted-foreground">
            Weekly Quantum Edge · Admin
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <h1 className="font-serif text-3xl md:text-4xl tracking-tight">Send weekly follow-up</h1>
        <p className="mt-4 text-sm text-muted-foreground max-w-lg leading-relaxed">
          Paste the recording link, add a brief note about this week's session, and send to all active members. Members appear in the count once their Stripe subscription is active.
        </p>

        <form onSubmit={send} className="mt-10 grid gap-6">
          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Admin token</label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onBlur={loadMemberCount}
              className="mt-2 block w-full rounded-lg border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Set in backend .env as WQE_ADMIN_TOKEN"
              required
              data-testid="wqe-admin-token"
            />
            {memberCount !== null && (
              <p className="mt-2 text-xs text-brand-turquoise" data-testid="wqe-admin-member-count">
                {memberCount} active members
              </p>
            )}
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Recording URL</label>
            <input
              type="url"
              value={recordingUrl}
              onChange={(e) => setRecordingUrl(e.target.value)}
              className="mt-2 block w-full rounded-lg border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://www.youtube.com/watch?v=..."
              required
              data-testid="wqe-admin-recording"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              This week's topic (one line)
            </label>
            <input
              type="text"
              value={topicSummary}
              onChange={(e) => setTopicSummary(e.target.value)}
              className="mt-2 block w-full rounded-lg border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Living from the As-Me level in everyday decisions"
              required
              data-testid="wqe-admin-topic"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Personal note (optional — a few sentences from you)
            </label>
            <textarea
              value={personalNote}
              onChange={(e) => setPersonalNote(e.target.value)}
              rows={4}
              className="mt-2 block w-full rounded-lg border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-serif leading-[1.7]"
              placeholder="Something you noticed during the session…"
              data-testid="wqe-admin-note"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Reflection or action for the week (optional)
            </label>
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              rows={3}
              className="mt-2 block w-full rounded-lg border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary font-serif leading-[1.7]"
              placeholder="A simple prompt or invitation…"
              data-testid="wqe-admin-reflection"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
              Announcement (optional)
            </label>
            <textarea
              value={announcement}
              onChange={(e) => setAnnouncement(e.target.value)}
              rows={2}
              className="mt-2 block w-full rounded-lg border border-border bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., No session next week — I'll be traveling."
              data-testid="wqe-admin-announcement"
            />
          </div>

          <div className="pt-2 flex items-center gap-4">
            <Button
              type="submit"
              disabled={sending}
              className="rounded-full bg-primary hover:bg-primary text-primary-foreground btn-lift h-11 px-6"
              data-testid="wqe-admin-send"
            >
              {sending ? "Sending…" : "Send follow-up email to all members"}
            </Button>
          </div>

          {status && (
            <p
              className={`text-sm ${status.ok ? "text-brand-turquoise" : "text-destructive"}`}
              data-testid="wqe-admin-status"
            >
              {status.message}
            </p>
          )}
        </form>
      </main>
    </div>
  );
}
